import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Camera, Loader2, Save, Sparkles } from "lucide-react";
import { toast } from "sonner";
import AdminNav from "@/components/AdminNav";
import api from "@/lib/api";
import { absolutePhotoUrl } from "@/hooks/useProfile";

export default function AdminProfile() {
  const fileRef = useRef(null);
  const [me, setMe] = useState(null);
  const [profile, setProfile] = useState({ name: "", phone: "", email: "", bio: "", photo_url: "" });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    try {
      const [mRes, pRes] = await Promise.all([api.get("/auth/me"), api.get("/profile")]);
      setMe(mRes.data);
      setProfile(pRes.data);
    } catch (e) {
      toast.error("Could not load profile");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/profile", {
        name: profile.name, phone: profile.phone, email: profile.email, bio: profile.bio,
      });
      setProfile(data);
      toast.success("Profile updated");
    } catch (e) {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/profile/photo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setProfile((p) => ({ ...p, photo_url: data.photo_url }));
      toast.success("Photo uploaded & enhanced");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const photoSrc = profile.photo_url ? `${absolutePhotoUrl(profile.photo_url)}?t=${Date.now()}` : null;

  return (
    <div style={{ background: "var(--paper)", minHeight: "100vh" }}>
      <AdminNav meName={me?.name} />
      <main className="max-w-[1200px] mx-auto px-6 md:px-10 py-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-10"
        >
          <div className="mono-eyebrow">§ Profile</div>
          <h1 className="serif text-4xl md:text-5xl mt-2" style={{ letterSpacing: "-0.02em" }}>
            The details visitors see.
          </h1>
          <p className="mt-3 text-sm" style={{ color: "var(--mute)" }}>
            Photo, contact and short bio — updated live across the site.
          </p>
        </motion.div>

        <div className="grid grid-cols-12 gap-6">
          {/* Photo card */}
          <section className="col-span-12 md:col-span-4 p-6 border hairline" style={{ background: "#fff" }}>
            <div className="mono-eyebrow">Photo</div>
            <div className="mt-4 aspect-[3/4] overflow-hidden border hairline" style={{ background: "var(--cream-2)" }}>
              {photoSrc ? (
                <img src={photoSrc} alt="Profile" className="editorial-img w-full h-full object-cover" data-testid="admin-profile-photo" />
              ) : (
                <div className="w-full h-full flex items-center justify-center" style={{ color: "var(--mute)" }}>
                  <Camera size={32} strokeWidth={1} />
                </div>
              )}
            </div>
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={uploadPhoto}
              className="hidden"
              data-testid="admin-photo-input"
            />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="btn-terra w-full justify-center mt-4"
              data-testid="admin-upload-photo-btn"
            >
              {uploading ? <Loader2 size={14} className="animate-spin" /> : <Camera size={14} />}
              {uploading ? "Enhancing…" : "Upload new photo"}
            </button>
            <div className="mt-4 flex items-start gap-2 text-xs" style={{ color: "var(--mute)" }}>
              <Sparkles size={12} className="mt-0.5 flex-shrink-0" style={{ color: "var(--terra)" }} />
              <span>Photos are auto-sharpened and upscaled on upload for a crisper editorial feel.</span>
            </div>
          </section>

          {/* Details card */}
          <section className="col-span-12 md:col-span-8 p-6 md:p-8 border hairline space-y-6" style={{ background: "#fff" }}>
            <div>
              <label className="mono-eyebrow">Display name</label>
              <input
                data-testid="admin-name-input"
                value={profile.name || ""}
                onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                className="mt-2 w-full p-4 border hairline bg-white focus:outline-none focus:border-black"
                style={{ borderRadius: 2 }}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="mono-eyebrow">Phone</label>
                <input
                  data-testid="admin-phone-input"
                  value={profile.phone || ""}
                  onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                  className="mt-2 w-full p-4 border hairline bg-white focus:outline-none focus:border-black"
                  style={{ borderRadius: 2 }}
                />
              </div>
              <div>
                <label className="mono-eyebrow">Email</label>
                <input
                  data-testid="admin-email-profile-input"
                  type="email"
                  value={profile.email || ""}
                  onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                  className="mt-2 w-full p-4 border hairline bg-white focus:outline-none focus:border-black"
                  style={{ borderRadius: 2 }}
                />
              </div>
            </div>
            <div>
              <label className="mono-eyebrow">Short bio (shown in hero)</label>
              <textarea
                data-testid="admin-bio-input"
                rows={5}
                value={profile.bio || ""}
                onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
                className="mt-2 w-full p-4 border hairline bg-white focus:outline-none focus:border-black"
                style={{ borderRadius: 2, fontFamily: "Manrope, sans-serif" }}
              />
            </div>
            <div className="flex justify-end pt-4 border-t hairline">
              <button
                onClick={save}
                disabled={saving}
                className="btn-terra"
                data-testid="admin-save-profile-btn"
              >
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                {saving ? "Saving…" : "Save changes"}
              </button>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}
